import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '../services/api';
import {
  CALENDARS, calendarOf, getRange, formatTitle, shiftCursor, startOfDay, addDays,
  startOfWeek, dateKey, holidaysByDate, parseEvent,
} from '../lib/calendar';
import TimeGrid from '../components/calendar/TimeGrid';
import MonthGrid from '../components/calendar/MonthGrid';
import AgendaView from '../components/calendar/AgendaView';
import MiniMonth from '../components/calendar/MiniMonth';
import EventDialog from '../components/calendar/EventDialog';
import EventPanel from '../components/calendar/EventPanel';

const VIEWS = [
  { id: 'day', label: 'Gün' },
  { id: 'week', label: 'Hafta' },
  { id: 'month', label: 'Ay' },
  { id: 'agenda', label: 'Ajanda' },
];

const readPref = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const writePref = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* tercih saklanamadı, sorun değil */ }
};

export const CalendarPage = ({ onCompose }) => {
  const [view, setView] = useState(() => {
    const saved = readPref('calendar.view', null);
    if (VIEWS.some((v) => v.id === saved)) return saved;
    return window.innerWidth < 768 ? 'day' : 'week';
  });
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [hidden, setHidden] = useState(() => readPref('calendar.hidden', []));
  const [showHolidays, setShowHolidays] = useState(() => readPref('calendar.holidays', true));
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState({ open: false, event: null, defaults: null });
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const requestRef = useRef(0);

  const range = useMemo(() => getRange(view, cursor), [view, cursor]);

  const loadEvents = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    try {
      const data = await api.getEventsRange(range.start, range.end);
      if (request !== requestRef.current) return; // daha yeni bir istek var
      setEvents((data.events || []).map(parseEvent));
      setLoadError('');
    } catch (err) {
      if (request !== requestRef.current) return;
      setLoadError(err.message || 'Etkinlikler yüklenemedi');
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    api.getContacts(null, false, 1, 100)
      .then((res) => setContacts(res.contacts || []))
      .catch(() => {});
  }, []);

  useEffect(() => writePref('calendar.view', view), [view]);
  useEffect(() => writePref('calendar.hidden', hidden), [hidden]);
  useEffect(() => writePref('calendar.holidays', showHolidays), [showHolidays]);

  const visibleEvents = useMemo(
    () => events.filter((e) => !hidden.includes(calendarOf(e.color).id)),
    [events, hidden],
  );
  const selectedEvent = events.find((e) => e.id === selectedId) || null;

  const holidays = useMemo(() => {
    if (!showHolidays) return {};
    const years = new Set([range.start.getFullYear(), addDays(range.end, -1).getFullYear()]);
    return holidaysByDate([...years]);
  }, [showHolidays, range]);

  const days = useMemo(() => {
    if (view === 'day') return [startOfDay(cursor)];
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [view, cursor]);

  const eventDays = useMemo(() => {
    const set = new Set();
    events.forEach((e) => {
      const last = e.all_day ? new Date(e.end.getTime() - 1) : e.end;
      for (let d = startOfDay(e.start); d <= last && set.size < 4000; d = addDays(d, 1)) set.add(dateKey(d));
    });
    return set;
  }, [events]);

  const toggleCalendar = (id) => setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));

  const openCreate = (start, extra = {}) => setDialog({ open: true, event: null, defaults: { start: start || nextHour(), ...extra } });
  const openEdit = (event) => setDialog({ open: true, event, defaults: null });

  const handleSave = async (payload, existing) => {
    if (existing) {
      await api.updateEvent(existing.id, payload);
      toast.success('Etkinlik güncellendi');
    } else {
      await api.createEvent(payload);
      toast.success('Etkinlik oluşturuldu');
    }
    await loadEvents();
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.deleteEvent(toDelete.id);
      toast.success('Etkinlik silindi');
      if (selectedId === toDelete.id) setSelectedId(null);
      setToDelete(null);
      await loadEvents();
    } catch (err) {
      toast.error(err.message || 'Etkinlik silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  const emailAttendees = (event) => {
    onCompose?.({ mode: 'new', to: (event.attendees || []).map((email) => ({ name: email, email })), subject: event.title });
  };

  const goToday = () => setCursor(startOfDay(new Date()));
  const pickDay = (day) => setCursor(startOfDay(day));
  const openDay = (day) => { setCursor(startOfDay(day)); setView('day'); };

  return (
    <div className="flex-1 min-w-0 flex bg-background" data-testid="calendar-page">
      {/* Sol panel */}
      <aside className="hidden lg:flex w-[264px] shrink-0 flex-col gap-6 border-r border-border p-4 overflow-y-auto">
        <Button className="w-full" onClick={() => openCreate()} data-testid="new-event-btn"><Plus className="h-4 w-4 mr-2" /> Yeni etkinlik</Button>
        <MiniMonth cursor={cursor} rangeStart={range.start} rangeEnd={range.end} eventDays={eventDays} onPick={pickDay} />
        <div>
          <h3 className="text-sm font-semibold mb-3">Takvimlerim</h3>
          <ul className="space-y-2">
            {CALENDARS.map((c) => (
              <li key={c.id}>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className={`h-4 w-4 rounded ${c.accent}`}
                    checked={!hidden.includes(c.id)}
                    onChange={() => toggleCalendar(c.id)}
                    data-testid={`calendar-toggle-${c.id}`}
                  />
                  <span className={`h-2.5 w-2.5 rounded-full ${c.dot}`} />
                  {c.name}
                </label>
              </li>
            ))}
          </ul>
        </div>
        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
          Resmî tatilleri göster
          <Switch checked={showHolidays} onCheckedChange={setShowHolidays} data-testid="holiday-toggle" />
        </label>
      </aside>

      {/* Ana alan */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <Button variant="outline" size="sm" onClick={goToday} data-testid="today-btn">Bugün</Button>
          <div className="flex">
            <Button variant="ghost" size="icon" onClick={() => setCursor(shiftCursor(view, cursor, -1))} aria-label="Önceki" data-testid="prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setCursor(shiftCursor(view, cursor, 1))} aria-label="Sonraki" data-testid="next-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <h1 className="text-lg font-semibold mr-auto" data-testid="calendar-title">{formatTitle(view, cursor)}</h1>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Yükleniyor" />}
          <div className="inline-flex rounded-md border border-border p-0.5" role="tablist" aria-label="Görünüm">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                role="tab"
                aria-selected={view === v.id}
                onClick={() => setView(v.id)}
                className={`px-3 py-1 text-sm rounded transition-colors ${view === v.id ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}
                data-testid={`view-${v.id}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <Button size="icon" className="lg:hidden" onClick={() => openCreate()} aria-label="Yeni etkinlik" data-testid="new-event-btn-mobile"><Plus className="h-4 w-4" /></Button>
        </header>

        {loadError && (
          <div className="mx-4 mt-3 flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm" role="alert" data-testid="calendar-error">
            <span>{loadError}</span>
            <Button size="sm" variant="outline" onClick={loadEvents}>Tekrar dene</Button>
          </div>
        )}

        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            {(view === 'week' || view === 'day') && (
              <TimeGrid days={days} events={visibleEvents} holidays={holidays} onSlotClick={(d) => openCreate(d)} onEventClick={(e) => setSelectedId(e.id)} selectedId={selectedId} />
            )}
            {view === 'month' && (
              <MonthGrid cursor={cursor} events={visibleEvents} holidays={holidays} onDayClick={openDay} onSlotClick={(d) => openCreate(d)} onEventClick={(e) => setSelectedId(e.id)} selectedId={selectedId} />
            )}
            {view === 'agenda' && (
              <AgendaView cursor={cursor} events={visibleEvents} holidays={holidays} onEventClick={(e) => setSelectedId(e.id)} selectedId={selectedId} />
            )}
          </div>

          {selectedEvent && (
            <div className="fixed inset-0 z-40 bg-background md:static md:z-auto md:flex md:w-auto">
              <EventPanel event={selectedEvent} onClose={() => setSelectedId(null)} onEdit={openEdit} onDelete={setToDelete} onEmailAttendees={emailAttendees} />
            </div>
          )}
        </div>
      </div>

      <EventDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        event={dialog.event}
        defaults={dialog.defaults}
        contacts={contacts}
        onSave={handleSave}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Etkinlik silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” kalıcı olarak silinecek. Katılımcılara iptal bildirimi gönderilmez.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const nextHour = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours() + 1, 0);
};

export default CalendarPage;
